import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Box,
  Skeleton,
  Divider
} from '@mui/material';
import {
  DirectionsWalk,
  EmojiEvents,
  Group,
  TrendingUp,
  CheckCircle,
  Timeline
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const RecentActivity = ({ data, loading }) => {
  const getActivityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'steps':
      case 'walk':
        return <DirectionsWalk />;
      case 'challenge':
      case 'achievement':
        return <EmojiEvents />;
      case 'team':
        return <Group />;
      case 'goal':
      case 'milestone':
        return <CheckCircle />;
      case 'progress':
        return <TrendingUp />;
      default:
        return <Timeline />;
    }
  };

  const getActivityColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'steps':
      case 'walk':
        return '#4CAF50';
      case 'challenge':
      case 'achievement':
        return '#FF9800';
      case 'team':
        return '#2196F3';
      case 'goal':
      case 'milestone':
        return '#9C27B0';
      case 'progress':
        return '#00BCD4';
      default:
        return '#757575';
    }
  };

  const formatActivityTime = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Recently';
    }
  };

  const renderActivityDescription = (activity) => {
    const { type, description, details } = activity;
    
    if (description) return description;
    
    // Generate description based on activity type and details
    switch (type?.toLowerCase()) {
      case 'steps':
        return `Recorded ${details?.steps?.toLocaleString() || 'new'} steps`;
      case 'challenge':
        return `Joined challenge: ${details?.challengeName || 'New Challenge'}`;
      case 'achievement':
        return `Unlocked achievement: ${details?.achievementName || 'Goal Reached'}`;
      case 'goal':
        return `Reached daily goal of ${details?.goal?.toLocaleString() || '10,000'} steps`;
      case 'team':
        return `Joined team: ${details?.teamName || 'New Team'}`;
      default:
        return 'New activity recorded';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <List>
            {[1, 2, 3, 4, 5].map((item) => (
              <ListItem key={item}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton width="60%" />}
                  secondary={<Skeleton width="40%" />}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            py={4}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              No recent activity to display
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Start tracking your steps to see your activity here
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Recent Activity
          </Typography>
          <Chip 
            label={`${data.length} recent`} 
            size="small" 
            variant="outlined"
          />
        </Box>
        
        <List disablePadding>
          {data.slice(0, 10).map((activity, index) => (
            <React.Fragment key={activity.id || index}>
              <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${getActivityColor(activity.type)}20`,
                      color: getActivityColor(activity.type)
                    }}
                  >
                    {getActivityIcon(activity.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">
                        {renderActivityDescription(activity)}
                      </Typography>
                      {activity.isNew && (
                        <Chip 
                          label="New" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {formatActivityTime(activity.timestamp || activity.createdAt)}
                      </Typography>
                      {activity.source && (
                        <Chip 
                          label={activity.source} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
              {index < data.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
        
        {data.length > 10 && (
          <Box textAlign="center" mt={2}>
            <Typography variant="caption" color="text.secondary">
              Showing 10 of {data.length} activities
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivity;